import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  DisconnectOutlined,
  LinkOutlined,
  ReloadOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import xeroIntegrationService from '../../../services/xeroIntegrationService';

const { Title, Text } = Typography;

const statusColor = (status) => {
  if (status === 'connected') return 'green';
  if (status === 'expired') return 'orange';
  return 'red';
};

export default function XeroIntegrationPage() {
  const [accounts, setAccounts] = useState([]);
  const [legacyConnections, setLegacyConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accountResponse, legacyResponse] = await Promise.all([
        xeroIntegrationService.listAccounts(),
        xeroIntegrationService.listConnections().catch(() => ({ data: [] })),
      ]);
      setAccounts(accountResponse?.data || []);
      setLegacyConnections(legacyResponse?.data || legacyResponse?.connections || []);
    } catch (error) {
      message.error(error?.message || 'Unable to load Xero accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connectedCount = Number(params.get('accounts_connected') || 0);
    if (connectedCount > 0) {
      message.success(`${connectedCount} Xero account${connectedCount === 1 ? '' : 's'} connected`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const updateLocal = useCallback((accountId, field, value) => {
    setAccounts(current => current.map(account => (
      Number(account.id) === Number(accountId)
        ? { ...account, [field]: value }
        : field === 'is_default' && value
          ? { ...account, is_default: false }
          : account
    )));
  }, []);

  const connect = async (accountId, oauthApp = 'xero') => {
    try {
      const connectingKey = accountId || oauthApp;
      setConnecting(connectingKey);
      const response = await xeroIntegrationService.getAccountConnectUrl(accountId, oauthApp);
      const url = response?.url || response?.data?.url;
      if (!url) throw new Error('Xero did not return a connection URL');
      window.location.href = url;
    } catch (error) {
      setConnecting(false);
      message.error(error?.message || 'Unable to start Xero connection');
    }
  };

  const save = async (account) => {
    try {
      setSavingId(account.id);
      await xeroIntegrationService.updateAccount(account.id, {
        display_name: account.display_name,
        is_active: Boolean(account.is_active),
        is_default: Boolean(account.is_default),
      });
      message.success(`${account.display_name} saved`);
      await load();
    } catch (error) {
      message.error(error?.message || 'Unable to save Xero account');
    } finally {
      setSavingId(null);
    }
  };

  const disconnect = (account) => {
    Modal.confirm({
      title: `Disconnect ${account.display_name}?`,
      content: 'Existing invoices keep their account link. New invoices will no longer be able to select this account until it is reconnected.',
      okText: 'Disconnect',
      okButtonProps: { danger: true },
      onOk: async () => {
        await xeroIntegrationService.disconnectAccount(account.id);
        message.success(`${account.display_name} disconnected`);
        await load();
      },
    });
  };

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <Title level={3} style={{ margin: 0 }}>Xero Accounts</Title>
          <Text type="secondary">
            Connect each Xero organisation once. Admins choose the receiving account when creating an invoice.
          </Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          <Button
            type="primary"
            icon={<LinkOutlined />}
            onClick={() => connect(null, 'xero')}
            loading={connecting === 'xero'}
          >
            Connect Golden Group Xero
          </Button>
          <Button
            type="primary"
            icon={<LinkOutlined />}
            onClick={() => connect(null, 'xero2')}
            loading={connecting === 'xero2'}
          >
            Connect Linen Xero
          </Button>
        </Space>
      </div>

      <Alert
        className="mb-4"
        type="info"
        showIcon
        message="Invoice routing is saved per invoice"
        description="Changing the default only affects the next invoice popup. It never moves an existing invoice to another Xero organisation."
      />

      {!loading && accounts.length === 0 ? (
        <Alert
          className="mb-4"
          type="warning"
          showIcon
          message="No system Xero account is configured"
          description="Connect the Golden Group account, then connect the second Xero account. Legacy client connections remain available for old invoices."
        />
      ) : null}

      <Row gutter={[16, 16]}>
        {accounts.map(account => (
          <Col xs={24} xl={12} key={account.id}>
            <Card
              loading={loading}
              title={(
                <Space wrap>
                  <span>{account.display_name}</span>
                  <Tag color={statusColor(account.status)}>{account.status}</Tag>
                  <Tag>{account.oauth_app === 'xero2' ? 'Linen app' : 'Golden app'}</Tag>
                  {account.is_default ? <Tag color="blue">Default</Tag> : null}
                </Space>
              )}
              extra={(
                <Button
                  icon={<LinkOutlined />}
                  onClick={() => connect(account.id)}
                  loading={connecting === account.id}
                >
                  Reconnect
                </Button>
              )}
            >
              <Form layout="vertical">
                <Form.Item label="Account name" required>
                  <Input
                    value={account.display_name}
                    onChange={event => updateLocal(account.id, 'display_name', event.target.value)}
                  />
                </Form.Item>
                <Form.Item label="Xero organisation">
                  <Input value={account.xero_tenant_name || account.xero_tenant_id} disabled />
                </Form.Item>
                <Space size="large" wrap>
                  <Form.Item label="Available on invoices">
                    <Switch
                      checked={Boolean(account.is_active)}
                      onChange={checked => updateLocal(account.id, 'is_active', checked)}
                      checkedChildren="Active"
                      unCheckedChildren="Inactive"
                    />
                  </Form.Item>
                  <Form.Item label="Default in popup">
                    <Switch
                      checked={Boolean(account.is_default)}
                      disabled={!account.is_active || account.status !== 'connected'}
                      onChange={checked => updateLocal(account.id, 'is_default', checked)}
                    />
                  </Form.Item>
                </Space>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={savingId === account.id}
                    onClick={() => save(account)}
                  >
                    Save account
                  </Button>
                  <Button
                    danger
                    icon={<DisconnectOutlined />}
                    disabled={account.status === 'disconnected'}
                    onClick={() => disconnect(account)}
                  >
                    Disconnect
                  </Button>
                </div>
              </Form>
            </Card>
          </Col>
        ))}
      </Row>

      {legacyConnections.length > 0 ? (
        <Card className="mt-4" title="Legacy client connections">
          <Text type="secondary">
            Preserved for invoices created before system Xero Accounts. New invoices use the account cards above.
          </Text>
          <Table
            className="mt-3"
            rowKey={(row) => `${row.client_id}-${row.xero_tenant_id || 'tenant'}`}
            dataSource={legacyConnections}
            pagination={{ pageSize: 10 }}
            columns={[
              { title: 'Client', dataIndex: 'client_name' },
              { title: 'Email', dataIndex: 'client_email', render: value => value || '—' },
              {
                title: 'Status',
                dataIndex: 'status',
                render: value => <Tag color={statusColor(value)}>{value}</Tag>,
              },
              {
                title: 'Organisation',
                dataIndex: 'xero_tenant_name',
                render: (value, row) => value || row.xero_tenant_id || '—',
              },
            ]}
          />
        </Card>
      ) : null}
    </div>
  );
}
